/**
 * Profile sharing — native share sheet with a clipboard fallback.
 *
 * Exposed on window because the profile action buttons call it from Alpine
 * @click bindings rendered in
 * profile/sections/header/action-buttons-partial.twig.
 */

/**
 * Rejection names that do not warrant a fallback, because one would talk over a
 * sheet already on screen: InvalidStateError is a second share raised while the
 * first sheet is open, and AbortError is the user dismissing it. The Web Share
 * API also reports "no share target available" as AbortError, so that case is
 * swallowed with it — the two are indistinguishable from here, and treating
 * every AbortError as a failure would raise a prompt behind every dismissal.
 */
const HANDLED_SHARE_REJECTIONS = ['AbortError', 'InvalidStateError'];

/**
 * I18n.t() returns the lookup key itself when an entry is missing, so compare
 * against the key to decide whether a usable translation came back. An empty
 * string is treated as a miss too — window.trans is stubbed to return one until
 * the i18n module evaluates.
 */
function translate(domain, key, fallback) {
    if (typeof window.trans !== 'function') {
        return fallback;
    }

    const text = window.trans(domain, key);

    return (typeof text === 'string' && text.trim() && text !== `${domain}.${key}`) ? text : fallback;
}

/**
 * Joins the share-text fragments with exactly one space. Several locales carry
 * their own padding inside the translated fragment (id_ID renders
 * "Lihat profil " and " di InterPals"), which would otherwise double up.
 */
function joinFragments(fragments) {
    return fragments.map((fragment) => fragment.trim()).filter(Boolean).join(' ');
}

/**
 * Last resort when neither the share sheet nor the clipboard is available: put
 * the URL somewhere the user can copy it by hand, so that no path through the
 * button leaves the tap without a result.
 *
 * The label is built from the username rather than the display name. It lands in
 * browser chrome attributed to the origin, and a display name is free text — one
 * written to read as a message from the site would be indistinguishable from one.
 */
function offerUrlManually(url, username) {
    if (typeof window.prompt !== 'function') {
        console.error('[share-profile] no way to surface the profile URL:', url);
        return;
    }

    const label = translate('profile', 'share_profile', 'Share @{username}')
        .split('{username}').join(username);

    window.prompt(label, url);
}

function copyProfileUrl(url, username) {
    if (!navigator.clipboard || !navigator.clipboard.writeText) {
        offerUrlManually(url, username);
        return;
    }

    navigator.clipboard.writeText(url)
        .then(() => {
            if (typeof window.showSuccess === 'function') {
                window.showSuccess(translate('common', 'copied', 'Copied!'));
            } else {
                offerUrlManually(url, username);
            }
        })
        .catch((error) => {
            // Safari only permits a clipboard write during the gesture that
            // triggered it, so a write reached from the share sheet's rejection
            // handler rather than from the click itself is refused.
            console.error('[share-profile] copy failed:', error);
            offerUrlManually(url, username);
        });
}

/**
 * @param {string} username Profile owner's username, used to build the URL.
 * @param {string} name     Display name used in the share text.
 */
export function shareProfile(username, name) {
    if (!username) {
        return;
    }

    const url = `${window.location.origin}/${encodeURIComponent(username)}`;
    const displayName = (name && name.trim()) || username;
    const text = joinFragments([
        translate('profile', 'share_text_prefix', 'Check out'),
        displayName,
        translate('profile', 'share_text_suffix', 'on InterPals'),
    ]);

    if (!navigator.share) {
        copyProfileUrl(url, username);
        return;
    }

    // A share sheet that throws instead of rejecting — an in-app browser with a
    // partial navigator.share shim — must reach the fallback too, so guard the
    // call as well as its promise.
    try {
        navigator.share({ title: displayName, text, url }).catch((error) => {
            if (error && HANDLED_SHARE_REJECTIONS.indexOf(error.name) !== -1) {
                return;
            }

            copyProfileUrl(url, username);
        });
    } catch (error) {
        console.error('[share-profile] share failed:', error);
        copyProfileUrl(url, username);
    }
}

if (typeof window !== 'undefined') {
    window.shareProfile = shareProfile;
}
